import { baseService } from "src/store/base.store";
import { handleFirebaseError } from "src/store";
import { userRepository } from "src/features/users/repositories/user.repository";
import {
  jobApplicationsRepository,
  jobCandidatesRepository,
  jobHelpRequestsRepository,
  jobSearchRequestsRepository,
  publicJobsRepository,
  recruiterCvUploadsRepository,
} from "./repositories/recruiter.repository";
import {
  JobApplication,
  JobCandidate,
  JobHelpRequest,
  JobSearchRequest,
  PublicJob,
  RecruiterCv,
  RecruiterCvUpload,
  RecruiterKind,
  RecruiterRecordDetail,
  RecruiterRow,
} from "./types/recruiter.entity";
import { pickCv } from "./utils/recruiter.cv";
import { startWhenLabel, travelDistanceLabel } from "./utils/recruiter.labels";

type WithId<T> = T & { id: string };
type AnyRecord = WithId<JobApplication> | WithId<JobHelpRequest> | WithId<JobSearchRequest>;
type Learner = { name: string | null; email: string | null };

const repositoryFor = (kind: RecruiterKind) => {
  switch (kind) {
    case "applications":
      return jobApplicationsRepository;
    case "help-requests":
      return jobHelpRequestsRepository;
    case "searches":
      return jobSearchRequestsRepository;
  }
};

const toIso = (value: any): string => value?.toDate?.()?.toISOString?.() ?? "";

const SUMMARY_LENGTH = 120;

const excerpt = (text: string) =>
  text.length > SUMMARY_LENGTH ? `${text.slice(0, SUMMARY_LENGTH).trimEnd()}…` : text;

/**
 * Keeps Firebase's own message. The store's `executeQuery` flattens every
 * failure to "Something went wrong", and on this page the likeliest failure is
 * "Missing or insufficient permissions" — the rules not deployed yet, or an
 * EMPLOYEE account — which is exactly what whoever sees it needs to read.
 */
const failed = (error: unknown) => {
  console.error("[recruiter]", error);
  return handleFirebaseError(error, "Could not load recruiter records");
};

/** A vacancy the catalogue has withdrawn is still named, by its code. */
const vacancyOf = (record: { jobId: string; jobCode?: string }, job?: WithId<PublicJob>) => ({
  subject: job?.title ?? record.jobCode ?? record.jobId,
  subjectDetail: job?.employer ?? "No longer listed",
  jobListed: job?.status === "ACTIVE",
});

const toRow = (
  kind: RecruiterKind,
  record: AnyRecord,
  learner: Learner | undefined,
  job: WithId<PublicJob> | undefined
): RecruiterRow => {
  const base = {
    id: record.id,
    kind,
    userId: record.userId,
    learnerName: learner?.name ?? null,
    learnerEmail: learner?.email ?? null,
    createdAt: toIso(record.createdAt),
    expiresAt: toIso(record.expiresAt),
  };

  if (kind === "applications") {
    const application = record as WithId<JobApplication>;
    return {
      ...base,
      ...vacancyOf(application, job),
      summary: application.motivation ? excerpt(application.motivation) : "No motivation given",
    };
  }

  if (kind === "help-requests") {
    const help = record as WithId<JobHelpRequest>;
    const asked = help.questions?.length ?? 0;
    const open = help.unanswered?.length ?? 0;
    return {
      ...base,
      ...vacancyOf(help, job),
      summary: open
        ? `${open} unanswered of ${asked}`
        : asked
          ? `${asked} asked, all answered`
          : "Browsed without asking",
    };
  }

  const search = record as WithId<JobSearchRequest>;
  return {
    ...base,
    subject: search.desiredWork || "Not stated",
    subjectDetail:
      [startWhenLabel(search.startWhen), travelDistanceLabel(search.travelDistance)]
        .filter(Boolean)
        .join(" · ") || "—",
    jobListed: true,
    summary: "",
  };
};

/**
 * Learners and vacancies for one page, fetched once per page rather than once
 * per row: two batched reads for ten rows instead of twenty single ones.
 */
const joinPage = async (records: AnyRecord[]) => {
  const userIds = Array.from(new Set(records.map((r) => r.userId)));
  const jobIds = Array.from(
    new Set(records.map((r) => ("jobId" in r ? r.jobId : undefined)).filter((id): id is string => !!id))
  );

  const [users, jobs] = await Promise.all([
    userRepository.findByIds(userIds),
    publicJobsRepository.findByIds(jobIds),
  ]);

  const learners = new Map<string, Learner>(
    users.map((u: any) => [u.id, { name: u.name ?? null, email: u.email ?? null }])
  );
  const vacancies = new Map<string, WithId<PublicJob>>(jobs.map((j: any) => [j.id, j]));

  return { learners, vacancies };
};

/**
 * The CV for one record: the ledger read, then [pickCv] for the decision.
 *
 * Split that way because the decision is the part that was wrong and the part
 * worth testing. The I/O stays here; `recruiter.cv.ts` carries the reasoning.
 *
 * A failed ledger read is handed on as `null`, not rethrown. The collection is
 * ADMIN-readable only from taaly_backend `fix/recruiter-cv-per-conversation`, so
 * until that ruleset is live every environment refuses it. The rest of the
 * record is still worth showing, and the CV is still worth offering, as long as
 * the page does not claim to know which conversation it came from.
 */
const resolveCv = async (
  record: { userId: string; conversationId: string },
  candidate: JobCandidate | null
): Promise<RecruiterCv | null> => {
  let uploads: RecruiterCvUpload[] | null = null;

  try {
    uploads = await recruiterCvUploadsRepository.findByConversation(record.conversationId);
  } catch (error) {
    console.error("[recruiter] could not read the CV upload ledger:", error);
  }

  return pickCv({ userId: record.userId, uploads, candidateCvPath: candidate?.cvStoragePath });
};

export const recruiterService = baseService.injectEndpoints({
  endpoints: (builder) => ({
    getRecruiterRows: builder.query<
      RecruiterRow[],
      { where: { kind: RecruiterKind }; offset?: string; limit?: number }
    >({
      queryFn: async ({ where, offset, limit }) => {
        try {
          const records = (await repositoryFor(where.kind).find({
            offset,
            limit,
            orderBy: ["createdAt", "desc"],
          } as any)) as unknown as AnyRecord[];

          const { learners, vacancies } = await joinPage(records);

          return {
            data: records.map((record) =>
              toRow(
                where.kind,
                record,
                learners.get(record.userId),
                "jobId" in record ? vacancies.get(record.jobId) : undefined
              )
            ),
          };
        } catch (error) {
          return failed(error);
        }
      },
      providesTags: ["Recruiter"],
    }),

    countRecruiterRows: builder.query<number, { where: { kind: RecruiterKind } }>({
      queryFn: async ({ where }) => {
        try {
          return { data: await repositoryFor(where.kind).count() };
        } catch (error) {
          return failed(error);
        }
      },
      providesTags: ["Recruiter"],
    }),

    getRecruiterRecord: builder.query<RecruiterRecordDetail, { kind: RecruiterKind; id: string }>({
      queryFn: async ({ kind, id }) => {
        try {
          const record = (await repositoryFor(kind).findById(id)) as AnyRecord | null;

          if (!record) return { error: "This record no longer exists. It may have expired." };

          const jobId = "jobId" in record ? record.jobId : undefined;

          const [user, job, candidate] = await Promise.all([
            userRepository.findById(record.userId),
            jobId ? publicJobsRepository.findById(jobId) : Promise.resolve(null),
            jobCandidatesRepository.findById(record.userId),
          ]);

          const learner: Learner | undefined = user
            ? { name: (user as any).name ?? null, email: (user as any).email ?? null }
            : undefined;
          const vacancy = job ? ({ ...job, id: jobId } as WithId<PublicJob>) : undefined;

          // After the batch above, because it needs the candidate row as its
          // fallback. One extra read on a detail view nobody opens in a loop.
          const cv = await resolveCv(
            { userId: record.userId, conversationId: record.conversationId },
            candidate as JobCandidate | null
          );

          const row = toRow(kind, { ...record, id }, learner, vacancy);

          return {
            data: {
              ...row,
              conversationId: record.conversationId,
              jobId,
              jobCode: "jobCode" in record ? record.jobCode : undefined,
              job: vacancy ?? null,
              motivation: "motivation" in record ? record.motivation : undefined,
              questions: "questions" in record ? record.questions : undefined,
              unanswered: "unanswered" in record ? record.unanswered : undefined,
              desiredWork: "desiredWork" in record ? record.desiredWork : undefined,
              travelDistance: "travelDistance" in record ? record.travelDistance : undefined,
              startWhen: "startWhen" in record ? record.startWhen : undefined,
              candidate: candidate ?? null,
              cv,
            },
          };
        } catch (error) {
          return failed(error);
        }
      },
      providesTags: ["Recruiter"],
    }),
  }),
});

export const {
  useGetRecruiterRowsQuery,
  useCountRecruiterRowsQuery,
  useGetRecruiterRecordQuery,
} = recruiterService;
